import { MouseEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { NestedMenuItem } from "mui-nested-menu"
import { Menu, MenuItem } from "@mui/material"

import IconButton from "@/components/button/IconButton"
import { MenuActionType } from "@/app/report/common/type"
import Divider from "@/components/divider/Divider"
import Icon from "@/components/icon/Icon"
import { colorTokens } from "@/components/colorTokens"

import "./sectionMenu.scss"

export default function SectionMenu({ menuItems }: { menuItems: MenuActionType[] }) {
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [hoveredValue, setHoveredValue] = useState("")
  const [selectedValue, setSelectedValue] = useState("")
  const [menuDuration, setMenuDuration] = useState(300) // 가장 상위 메뉴의 duration 을 조절하여 하위 메뉴 close 시 바로 사라질 수 있도록 함.

  useEffect(() => {
    if (!anchorEl) {
      setMenuDuration(300) // default duration
    }
  }, [anchorEl])

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setMenuDuration(10)
    setAnchorEl(null)
    setHoveredValue("")
  }

  const groupedMenuItems = menuItems.reduce(
    (acc, item) => {
      // 1뎁스는 무조건 group 을 가져야 함
      if (!acc[item.group!]) {
        acc[item.group!] = []
      }
      acc[item.group!].push(item)

      return acc
    },
    {} as { [key: string]: MenuActionType[] },
  )

  const flattenArray = (items: MenuActionType[]): MenuActionType[] => {
    return items.flatMap((item) =>
      item.children ? [item, ...flattenArray(item.children)] : [item],
    )
  }

  const getBgColor = ({ value, children }: MenuActionType) => {
    if (!children) {
      return hoveredValue === value ? colorTokens.background["alpha-platinum-2"] : "white"
    } else {
      return flattenArray(children).some((child) => child.value === hoveredValue)
        ? colorTokens.background["alpha-platinum-2"]
        : "white"
    }
  }

  return (
    <>
      <IconButton type="ghost" icon="more-vertical" size="xs" onClick={handleOpen} />
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        transitionDuration={menuDuration}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        className="section-nested-menu-wrapper"
        disableAutoFocus
        disableEnforceFocus>
        {Object.entries(groupedMenuItems).map(([group, items], index) => {
          return (
            <div key={group}>
              {index !== 0 && <Divider />}
              <div className="nested-menu-group">
                <p>{t(group)}</p>
              </div>
              {items.map((menu) => (
                <NestedMenuChild
                  key={`${menu.group}-${menu.name}`}
                  menu={menu}
                  hoveredValue={hoveredValue}
                  setHoveredValue={setHoveredValue}
                  selectedValue={selectedValue}
                  setSelectedValue={setSelectedValue}
                  handleClose={handleClose}
                  getBgColor={getBgColor}
                />
              ))}
            </div>
          )
        })}
      </Menu>
    </>
  )
}

function NestedMenuChild({
  menu,
  setHoveredValue,
  hoveredValue,
  getBgColor,
  selectedValue,
  setSelectedValue,
  handleClose,
}: {
  menu: MenuActionType
  hoveredValue: string
  setHoveredValue: (value: string) => void
  selectedValue?: string
  setSelectedValue: (value: string) => void
  handleClose: () => void
  getBgColor: (menu: MenuActionType) => string
}) {
  const { t } = useTranslation()

  if (menu.children) {
    return (
      <NestedMenuItem
        key={menu.name}
        label={t(menu.name)}
        parentMenuOpen
        delay={0}
        onMouseEnter={() => setHoveredValue(menu.value)}
        onMouseLeave={() => hoveredValue === menu.value && setHoveredValue("")}
        sx={{
          backgroundColor: getBgColor(menu),
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          className: "section-nested-menu-child",
          slotProps: {
            paper: {
              sx: {
                mt: -1,
              },
            },
          },
        }}
        rightIcon={
          menu.children ? (
            <Icon type="arrow-right" size={16} color={colorTokens.field["ico-base"]} />
          ) : (
            <></>
          )
        }>
        {menu.children?.map((child) =>
          child.children ? (
            <NestedMenuItem
              key={child.name}
              label={t(child.name)}
              parentMenuOpen
              onMouseEnter={() => setHoveredValue(child.value)}
              onMouseLeave={() => hoveredValue === child.value && setHoveredValue("")}
              sx={{
                backgroundColor: getBgColor(child),
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "right",
                },
                className: "section-nested-menu-item",
                slotProps: {
                  paper: {
                    sx: {
                      mt: -1,
                    },
                  },
                },
              }}
              leftIcon={
                child.color ? (
                  <span
                    className="nested-menu-left-icon"
                    style={{
                      backgroundColor: child.color,
                    }}
                  />
                ) : (
                  <></>
                )
              }
              rightIcon={
                child.children ? (
                  <Icon type="arrow-right" size={16} color={colorTokens.field["ico-base"]} />
                ) : (
                  <></>
                )
              }>
              {child.children.map((subChild) => (
                <DefaultMenuItem
                  key={subChild.name}
                  menu={subChild}
                  hoveredValue={hoveredValue}
                  setHoveredValue={setHoveredValue}
                  selectedValue={selectedValue}
                  setSelectedValue={setSelectedValue}
                  handleClose={handleClose}
                  getBgColor={getBgColor}
                  isLast
                />
              ))}
            </NestedMenuItem>
          ) : (
            <DefaultMenuItem
              key={child.name}
              menu={child}
              hoveredValue={hoveredValue}
              setHoveredValue={setHoveredValue}
              selectedValue={selectedValue}
              setSelectedValue={setSelectedValue}
              handleClose={handleClose}
              getBgColor={getBgColor}
              isLast
            />
          ),
        )}
      </NestedMenuItem>
    )
  }

  return (
    <DefaultMenuItem
      key={menu.name}
      menu={menu}
      hoveredValue={hoveredValue}
      setHoveredValue={setHoveredValue}
      handleClose={handleClose}
      getBgColor={getBgColor}
    />
  )
}

function DefaultMenuItem({
  menu,
  handleClose,
  getBgColor,
  setHoveredValue,
  hoveredValue,
  selectedValue,
  setSelectedValue,
  isLast = false,
}: {
  menu: MenuActionType
  hoveredValue: string
  setHoveredValue: (value: string) => void
  handleClose: () => void
  getBgColor: (menu: MenuActionType) => string
  selectedValue?: string
  setSelectedValue?: (value: string) => void
  isLast?: boolean
}) {
  const { t } = useTranslation()

  return (
    <MenuItem
      key={menu.name}
      onClick={() => {
        menu.func?.()
        setSelectedValue?.(menu.value)
        handleClose()
      }}
      sx={{
        backgroundColor: getBgColor(menu),
      }}
      onMouseEnter={() => setHoveredValue(menu.value)}
      onMouseLeave={() => hoveredValue === menu.value && setHoveredValue("")}>
      <div className="nested-menu-selection-wrapper">
        <p>{t(menu.name)}</p>
        {isLast && selectedValue === menu.value && (
          <Icon type="check" size={16} color={colorTokens.field["ico-base"]} />
        )}
      </div>
    </MenuItem>
  )
}
